// ISDP CUSTOM: One-click "connect your AI tool" dialog.
//
// Collapses the old multi-step flow (mint a PAT → hand-assemble a `claude mcp add`
// command) into a single modal: press Connect, the dialog POSTs to the EXISTING
// /api/mcp/tokens mint API, holds the raw token in local component state ONLY
// (never localStorage / sessionStorage / URL), and renders the one-time secret plus
// tabbed, ready-to-paste snippets with the token already substituted.
//
// The mint API scopes the token's audience/products from the caller, so this dialog
// works unchanged for whoever can mint — internal staff (Settings) and entitled
// partners (API Access). The token is cleared on close and on unmount.
import { useCallback, useEffect, useState } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useCopyToClipboard } from '../feedback/useCopyToClipboard';
import { mcpMessages } from '../../i18n/mcp';
import { commonMessages } from '../../i18n/common';
import {
  buildConnectSnippets,
  type ConnectClient,
  type ConnectSnippets,
} from './connectSnippets';
import './McpAccessPage.css';

interface ConnectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional default token name (e.g. "Claude Code"). */
  defaultName?: string;
  /** Optional server key used in the generated config (defaults to draeger-devhub). */
  serverKey?: string;
}

const CLIENTS: ConnectClient[] = ['claudeCode', 'cursor', 'claudeDesktop', 'vscode'];

export const ConnectDialog = ({
  open,
  onClose,
  defaultName,
  serverKey,
}: ConnectDialogProps) => {
  const config = useApi(configApiRef);
  const { fetch: authFetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const base = config.getOptionalString('app.baseUrl') || backendUrl;
  const copy = useCopyToClipboard();
  const { t } = useTranslationRef(mcpMessages);
  const { t: tc } = useTranslationRef(commonMessages);

  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The raw token lives ONLY here, in component state, for the dialog's lifetime.
  const [token, setToken] = useState<string | null>(null);
  const [snippets, setSnippets] = useState<ConnectSnippets | null>(null);
  const [client, setClient] = useState<ConnectClient>('claudeCode');

  // Reset everything (and drop the token) whenever the dialog is closed. Also runs
  // on unmount via the same effect cleanup, so a stale secret never lingers.
  const reset = useCallback(() => {
    setMinting(false);
    setError(null);
    setToken(null);
    setSnippets(null);
    setClient('claudeCode');
  }, []);

  useEffect(() => {
    if (!open) reset();
    // On unmount, clear the in-memory token as a belt-and-braces measure.
    return () => {
      setToken(null);
      setSnippets(null);
    };
  }, [open, reset]);

  // Close on Escape while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleConnect = useCallback(async () => {
    setError(null);
    setMinting(true);
    try {
      const resp = await authFetch(`${backendUrl}/api/mcp/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: (defaultName || 'Claude Code').trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError((data && data.error) || t('connect.errMint'));
        return;
      }
      const raw = String(data.token || '');
      if (!raw) {
        setError(t('connect.errMint'));
        return;
      }
      setToken(raw);
      setSnippets(buildConnectSnippets(base, raw, serverKey));
    } catch {
      setError(t('connect.errMint'));
    } finally {
      setMinting(false);
    }
  }, [authFetch, backendUrl, base, defaultName, serverKey, t]);

  if (!open) return null;

  const clientLabel = (c: ConnectClient): string => {
    switch (c) {
      case 'claudeCode':
        return t('connect.clientClaudeCode');
      case 'cursor':
        return t('connect.clientCursor');
      case 'claudeDesktop':
        return t('connect.clientClaudeDesktop');
      case 'vscode':
        return t('connect.clientVscode');
      default:
        return c;
    }
  };

  const activeSnippet = snippets ? snippets[client] : '';

  return (
    <div
      className="devhub-connect__overlay"
      role="presentation"
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- modal dialog only stops propagation so clicks/keys don't bubble to the overlay's close handler */}
      <div
        className="devhub-connect"
        role="dialog"
        aria-modal="true"
        aria-label={t('connect.title')}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="devhub-connect__close"
          aria-label={tc('actions.close')}
          onClick={onClose}
        >
          <odx-icon name="core::close" size="sm" />
        </button>

        <header className="devhub-connect__head">
          <div className="devhub-connect__icon">
            <odx-icon name="core::service" size="lg" />
          </div>
          <div>
            <h1>{t('connect.title')}</h1>
            <p className="devhub-connect__lead">{t('connect.lead')}</p>
          </div>
        </header>

        {/* Pre-mint: an explicit Connect button (never mint on accidental open). */}
        {!token && (
          <div className="devhub-connect__mint">
            <p className="devhub-connect__muted">{t('connect.mintBody')}</p>
            <odx-button
              type="button"
              variant="primary"
              // `|| undefined` so React removes the attribute when falsy — a
              // boolean-false *update* on an <odx-*> element stringifies to
              // disabled="false", which the web component treats as disabled
              // (would strand the retry button after a failed mint).
              disabled={minting || undefined}
              onClick={handleConnect}
            >
              {minting ? t('connect.minting') : t('connect.connectBtn')}
            </odx-button>
            {error && (
              <p className="devhub-connect__error" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        {/* Post-mint: one-time secret + tabbed snippets. */}
        {token && snippets && (
          <>
            <section className="devhub-connect__secret-wrap">
              <h2>
                <odx-icon name="core::check" size="sm" /> {t('connect.secretTitle')}
              </h2>
              <p className="devhub-connect__warn">{t('connect.secretWarn')}</p>
              <div className="devhub-connect__secret">
                <code>{token}</code>
                <odx-button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => copy(token, t('connect.tokenLabel'))}
                >
                  {tc('actions.copy')}
                </odx-button>
              </div>
            </section>

            <section className="devhub-connect__snippets">
              <h2>{t('connect.snippetsTitle')}</h2>
              <div className="devhub-connect__tabs" role="tablist">
                {CLIENTS.map(c => (
                  <button
                    key={c}
                    type="button"
                    role="tab"
                    aria-selected={client === c}
                    className={`devhub-connect__tab ${client === c ? 'is-active' : ''}`}
                    onClick={() => setClient(c)}
                  >
                    {clientLabel(c)}
                  </button>
                ))}
              </div>
              <div className="devhub-connect__cmd">
                <pre className="devhub-connect__pre">{activeSnippet}</pre>
                <button
                  type="button"
                  className="devhub-connect__copy"
                  onClick={() => copy(activeSnippet, clientLabel(client))}
                >
                  {tc('actions.copy')}
                </button>
              </div>
              <p className="devhub-connect__muted">{t('connect.snippetsHint')}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};
